import { useState } from "react";
import { Check, Plus, Loader2, FolderPlus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { useCollections, useCafeCollectionMembership, LIST_NAME_SUGGESTIONS } from "@/hooks/useCollections";

interface AddToCollectionDialogProps {
  cafeId: string;
  cafeName: string;
  /** Optional trigger element. If omitted, control with `open`/`onOpenChange`. */
  trigger?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * Picker for organizing a saved cafe into named lists ("collections").
 * Membership toggles call into useCollections, which also guarantees the cafe
 * stays in user_favorites (the saved/list invariant). New lists can be created
 * inline without leaving the dialog.
 */
export function AddToCollectionDialog({
  cafeId,
  cafeName,
  trigger,
  open,
  onOpenChange,
}: AddToCollectionDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : internalOpen;
  const setOpen = isControlled ? (onOpenChange ?? (() => {})) : setInternalOpen;

  const {
    collections,
    isLoading: collectionsLoading,
    createCollection,
    addCafeToCollection,
    removeCafeFromCollection,
  } = useCollections();
  const {
    collectionIds,
    isLoading: membershipLoading,
    refresh: refreshMembership,
  } = useCafeCollectionMembership(isOpen ? cafeId : undefined);

  const membership = new Set(collectionIds);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  const toggle = async (collectionId: string) => {
    if (pendingId) return;
    setPendingId(collectionId);
    if (membership.has(collectionId)) {
      await removeCafeFromCollection(collectionId, cafeId);
    } else {
      await addCafeToCollection(collectionId, cafeId);
    }
    await refreshMembership();
    setPendingId(null);
  };

  const handleCreateNamed = async (rawName: string, fromSuggestion: boolean) => {
    const name = rawName.trim();
    if (!name || creating) return;
    setCreating(true);
    const created = await createCollection(name, {
      source: "add_to_list_dialog",
      fromSuggestion,
    });
    if (created) {
      await addCafeToCollection(created.id, cafeId);
      await refreshMembership();
      setNewName("");
      setShowCreate(false);
    }
    setCreating(false);
  };

  const handleCreate = () => handleCreateNamed(newName, false);

  const loading = collectionsLoading || membershipLoading;

  // Suggested list names, minus any the user already has (case-insensitive).
  const existingNames = new Set(collections.map((c) => c.name.trim().toLowerCase()));
  const suggestions = LIST_NAME_SUGGESTIONS.filter(
    (s) => !existingNames.has(s.toLowerCase())
  );

  return (
    <Dialog open={isOpen} onOpenChange={setOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="w-[calc(100%-2rem)] max-w-sm max-h-[85vh] supports-[height:1dvh]:max-h-[85dvh] overflow-y-auto rounded-2xl sm:rounded-2xl p-5">
        <DialogHeader>
          <DialogTitle className="font-display">Add to a list</DialogTitle>
          <DialogDescription>
            Organize <span className="font-medium text-foreground">{cafeName}</span> into your lists.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {collections.length > 0 && (
              <ScrollArea className="max-h-64 -mx-1 px-1">
                <div className="space-y-1">
                  {collections.map((c) => {
                    const isMember = membership.has(c.id);
                    const isPending = pendingId === c.id;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => toggle(c.id)}
                        disabled={isPending}
                        className={cn(
                          "w-full flex items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-left transition-colors min-h-[44px]",
                          isMember ? "bg-primary/10" : "hover:bg-secondary/50",
                          isPending && "opacity-60"
                        )}
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">{c.name}</p>
                          <p className="text-[11px] text-muted-foreground">
                            {c.cafeCount} {c.cafeCount === 1 ? "cafe" : "cafes"}
                          </p>
                        </div>
                        <span
                          className={cn(
                            "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border",
                            isMember
                              ? "border-primary bg-primary text-primary-foreground"
                              : "border-muted-foreground/40"
                          )}
                        >
                          {isPending ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : isMember ? (
                            <Check className="h-3 w-3" />
                          ) : null}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </ScrollArea>
            )}

            {collections.length === 0 && !showCreate && (
              <div className="flex flex-col items-center gap-2 py-6 text-center">
                <FolderPlus className="h-8 w-8 text-muted-foreground/50" />
                <p className="text-sm text-muted-foreground">
                  You don't have any lists yet. Create one to start organizing.
                </p>
              </div>
            )}

            {showCreate ? (
              <div className="space-y-2.5 pt-1">
                <p className="text-xs text-muted-foreground">
                  Name a list by how you'll use it — a vibe, a place, or an occasion.
                </p>
                {suggestions.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {suggestions.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => handleCreateNamed(s, true)}
                        disabled={creating}
                        className="rounded-full border border-border px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:bg-secondary/60 hover:text-foreground disabled:opacity-50"
                      >
                        + {s}
                      </button>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Input
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleCreate();
                      if (e.key === "Escape") {
                        setShowCreate(false);
                        setNewName("");
                      }
                    }}
                    onFocus={(e) => {
                      const el = e.currentTarget;
                      setTimeout(() => {
                        el.scrollIntoView({ behavior: "smooth", block: "center" });
                      }, 350);
                    }}
                    placeholder="Or type your own…"
                    maxLength={50}
                    className="h-10"
                  />
                  <Button onClick={handleCreate} disabled={!newName.trim() || creating} size="sm" className="shrink-0">
                    {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                variant="outline"
                onClick={() => setShowCreate(true)}
                className="w-full gap-2"
                size="sm"
              >
                <Plus className="h-4 w-4" />
                New list
              </Button>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
